import { useEffect, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  Skeleton,
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Select,
  SettingsGroup,
} from "@playbookai/ui";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "./AdminSection.js";
import {
  applyPendingRoleChange,
  assignableGroupsQueryKey,
  groupRoleMappingsQueryKey,
  listAssignableGroups,
  listGroupRoleMappings,
  listPendingRoleChanges,
  pendingRoleChangesQueryKey,
  retireGroupRoleMapping,
  setGroupRoleMapping,
  type MappableRole,
  type PendingRoleChange,
  type PendingRoleChangeBlock,
} from "../../api/admin-group-role-mapping-api.js";
import {
  AdminApiError,
  TRANSFORMATION_LEAD_ROLE,
  type TenantRole,
} from "../../api/admin-users-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { ADMIN_ROUTE, ADMIN_SECTION_ROUTES } from "../../routes.js";
import {
  TransformationLeadRemovalDialog,
  type TransformationLeadRemovalInput,
} from "./TransformationLeadRemovalDialog.js";

/**
 * Directory group roles — PB-136 / PPL-04.
 *
 * A rule says membership of a synced directory group implies a tenant role. The
 * page is deliberate about one thing above all: a rule PROPOSES, it never
 * applies. Every grant and every revocation is a button an administrator
 * presses, and a blocked proposal is shown with its reason rather than hidden,
 * because a silently short list is the failure mode that reads as "nothing to
 * do".
 *
 * Admin-only: it lives under the `/admin` tree, gated once by
 * `RequireTenantAdmin` in App.tsx (PB-42 / PB-48); the server remains the
 * authoritative gate.
 */

const BLOCK_COPY: Record<PendingRoleChangeBlock, string> = {
  "last-admin": "Blocked — this would leave the workspace with no admin.",
  "stale-group-data":
    "Blocked — the latest directory sync could not read group membership in full, so this may not be a real change.",
  "membership-not-active":
    "Blocked — this person is suspended or removed. Reactivate them first.",
};

const REASON_COPY: Record<PendingRoleChange["reason"], string> = {
  "in-mapped-group": "In a mapped group",
  "left-mapped-group": "No longer in a mapped group",
  "rule-retired": "The rule that granted this was retired",
};

/* A rule implies an ADDITIVE role (ADR-0078). `tenant-member` is not offered:
   every active member already holds it, so a rule granting it would propose
   nothing — and the old option that did exist here proposed exactly that. */
const ROLE_OPTIONS: { value: MappableRole; label: string }[] = [
  { value: "tenant-admin", label: "Admin" },
  { value: TRANSFORMATION_LEAD_ROLE, label: "Transformation Lead" },
];

const ROLE_LABEL: Record<TenantRole, string> = {
  "tenant-member": "Member",
  "tenant-admin": "Admin",
  [TRANSFORMATION_LEAD_ROLE]: "Transformation Lead",
};

function roleLabel(role: TenantRole): string {
  return ROLE_LABEL[role];
}

function asMappableRole(value: string): MappableRole {
  return value === TRANSFORMATION_LEAD_ROLE
    ? TRANSFORMATION_LEAD_ROLE
    : "tenant-admin";
}

function personLabel(change: PendingRoleChange): string {
  return change.displayName ?? change.email ?? change.userId;
}

/** Carries the non-null `blockedBy` through the filter, so no assertion is needed. */
type BlockedChange = PendingRoleChange & { blockedBy: PendingRoleChangeBlock };

function isBlocked(change: PendingRoleChange): change is BlockedChange {
  return change.blockedBy !== null;
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof AdminApiError ? cause.message : fallback;
}

export function DirectoryGroupRoles(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const claims = useAuthStore((state) => state.claims);
  const tenantId = claims?.tenantId ?? null;
  const [newGroupId, setNewGroupId] = useState("");
  const [newRole, setNewRole] = useState<MappableRole>("tenant-admin");
  /* A `transformation-lead` REVOCATION is the one proposal that cannot be
     applied with a click: the person's LAMA ownership has to go somewhere first
     (ADR-0078), so it opens the same transfer dialog the Members page uses and
     applies through the mapping endpoint with the replacement and the dialog's
     idempotency key. Fenced on the tenant like the Members page's dialogs. */
  const [leadRevocation, setLeadRevocation] = useState<{
    readonly tenantId: string | null;
    readonly change: PendingRoleChange;
  } | null>(null);
  const leadRevocationTarget =
    leadRevocation && leadRevocation.tenantId === tenantId
      ? leadRevocation.change
      : null;
  const [groupQuery, setGroupQuery] = useState("");
  const [debouncedGroupQuery, setDebouncedGroupQuery] = useState("");

  // Same debounce the Workspace directory search uses.
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedGroupQuery(groupQuery.trim()),
      300,
    );
    return () => clearTimeout(timer);
  }, [groupQuery]);

  // Paged, because a tenant can hold more rules than one page: without this a
  // rule past the first page could never be seen or retired.
  const rules = useInfiniteQuery({
    queryKey: groupRoleMappingsQueryKey(tenantId),
    queryFn: ({ pageParam }) => listGroupRoleMappings("active", pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const pending = useQuery({
    queryKey: pendingRoleChangesQueryKey(tenantId),
    queryFn: () => listPendingRoleChanges(),
  });
  // Server-backed search rather than a "load more": an Entra tenant can hold
  // hundreds of groups, and paging through them to find one is not how anybody
  // looks for a group by name. The search reaches any group regardless of page.
  const groups = useQuery({
    queryKey: assignableGroupsQueryKey(tenantId, debouncedGroupQuery),
    queryFn: () => listAssignableGroups({ query: debouncedGroupQuery }),
  });

  // The rules key is a prefix of both the pending and picker keys, and
  // invalidateQueries matches by prefix — so this refreshes all three, for this
  // tenant only. Any rule change moves the proposals and the picker's
  // "already mapped" state too.
  const refreshAll = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: groupRoleMappingsQueryKey(tenantId),
    });
  };

  const createRule = useMutation({
    mutationFn: ({ groupId, role }: { groupId: string; role: MappableRole }) =>
      setGroupRoleMapping(groupId, role),
    onSuccess: async () => {
      setNewGroupId("");
      await refreshAll();
    },
  });
  const changeRule = useMutation({
    mutationFn: ({ groupId, role }: { groupId: string; role: MappableRole }) =>
      setGroupRoleMapping(groupId, role),
    onSuccess: refreshAll,
  });
  const retireRule = useMutation({
    mutationFn: (groupId: string) => retireGroupRoleMapping(groupId),
    onSuccess: refreshAll,
  });
  const applyChange = useMutation({
    mutationFn: ({
      userId,
      kind,
      role,
      removal,
    }: {
      userId: string;
      kind: "grant" | "revoke";
      role: MappableRole;
      removal?: TransformationLeadRemovalInput;
    }) => applyPendingRoleChange(userId, kind, role, removal ?? {}),
    onSuccess: refreshAll,
  });

  const loadError =
    (rules.error ?? pending.error ?? groups.error)
      ? errorMessage(
          rules.error ?? pending.error ?? groups.error,
          "Could not load directory group roles.",
        )
      : null;
  const actionError =
    (createRule.error ??
    changeRule.error ??
    retireRule.error ??
    applyChange.error)
      ? errorMessage(
          createRule.error ??
            changeRule.error ??
            retireRule.error ??
            applyChange.error,
          "That change could not be applied.",
        )
      : null;

  const mappings = rules.data?.pages.flatMap((page) => page.mappings) ?? null;
  const changes = pending.data?.changes ?? [];
  const actionable = changes.filter((change) => !isBlocked(change));
  const blocked = changes.filter(isBlocked);
  // Only groups without a rule are offered; re-pointing an existing one is done
  // from its own row.
  const unmappedGroups = (groups.data?.groups ?? []).filter(
    (group) => group.mappedRole === null,
  );

  return (
    <AdminSection
      icon={ShieldCheck}
      title="Directory group roles"
      description="Use groups from your directory to decide who administers this workspace. A rule proposes a change; you approve it."
      /* This page has no entry in the admin rail — it is reachable only from
         Identity & directory — so without this the rail highlights nothing and
         there is no way back except the browser button. An explicit return
         affordance is the whole navigation for the page.

         `onNavigate` hands the click to the router (PB-4546): the crumb stays a
         real `<a href>`, so it can still be opened in a new tab, but a plain
         click no longer reloads the document. The trailing crumb names this
         page and carries `aria-current` — it is deliberately not a link. */
      breadcrumb={
        <Breadcrumb
          items={[
            {
              label: "Identity & directory",
              href: ADMIN_SECTION_ROUTES["identity"] ?? ADMIN_ROUTE,
            },
            { label: "Directory group roles" },
          ]}
          onNavigate={(href) => navigate(href)}
        />
      }
      meta={
        <>
          <AdminAboutCard title="About this page">
            A rule maps a directory group to a workspace role. The rule proposes
            each change and an admin approves it here, so nobody gains or loses
            a role without a person seeing it.
          </AdminAboutCard>
          <AdminRelatedCard ids={["members"]} />
        </>
      }
    >
      {/* "Something went wrong" named nothing and covered two different
          events. A failed READ can be retried; a failed ACTION cannot be
          retried by this button, so they are told apart. */}
      {loadError ? (
        <Alert tone="critical" title="Couldn't load this page">
          {loadError}
          <Button
            variant="secondary"
            onClick={() => {
              void rules.refetch();
              void pending.refetch();
              void groups.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : actionError ? (
        <Alert tone="critical" title="That did not work">
          {actionError}
        </Alert>
      ) : null}

      {/* One alert, not two. Since page truncation folds into `groupData`, the
          "incomplete membership" and "partial list" conditions are no longer
          independent — rendering both stacked two warnings that said the same
          thing. The cause line varies; the consequence is identical. */}
      {pending.data && !pending.data.groupData.groupDataTrusted ? (
        <Alert tone="watch" title="Group membership may be incomplete">
          {pending.data.truncated
            ? "There are more people than this page can evaluate at once, so this list may be partial"
            : "The latest directory sync could not read group membership in full"}
          {pending.data.groupData.reason
            ? ` (${pending.data.groupData.reason})`
            : ""}
          . New admins can still be approved, but removals are held back — an
          incomplete read looks the same as everyone having left a group.
        </Alert>
      ) : pending.data?.truncated ? (
        <Alert tone="watch" title="Showing a partial list">
          There are more people than this page can evaluate at once. Approve
          what is listed and reload to see the rest.
        </Alert>
      ) : null}

      <SettingsGroup
        title="Waiting for your approval"
        description="What your rules imply right now. Nothing changes until you approve it."
      >
        <Card>
          <CardBody>
            {pending.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : actionable.length === 0 ? (
              <EmptyState
                variant="no-results"
                title="Nothing waiting"
                description={
                  mappings && mappings.length > 0
                    ? "Everyone in your mapped groups already has the role their rule implies."
                    : "Add a rule below to start using directory groups for access."
                }
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {actionable.map((change) => (
                  <li
                    key={`${change.userId}-${change.kind}`}
                    className="flex flex-wrap items-center justify-between gap-3 border-b border-pb-border/70 pb-2 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {personLabel(change)}
                      </p>
                      <p className="text-xs text-pb-text-muted">
                        {/* Names the ADDITIVE role the proposal moves, not the
                            before/after base access — "Make Transformation
                            Lead", never "Make Member". */}
                        {change.kind === "grant"
                          ? `Make ${roleLabel(change.role)}`
                          : `Remove ${roleLabel(change.role)}`}
                        {" · "}
                        {REASON_COPY[change.reason]}
                        {change.groupDisplayName
                          ? ` · ${change.groupDisplayName}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      variant={
                        change.kind === "grant" ? "primary" : "secondary"
                      }
                      // Scoped to the acting row, so approving one person does
                      // not freeze every other control on the page.
                      disabled={
                        applyChange.isPending &&
                        applyChange.variables?.userId === change.userId &&
                        applyChange.variables?.kind === change.kind &&
                        applyChange.variables?.role === change.role
                      }
                      onClick={() => {
                        if (
                          change.kind === "revoke" &&
                          change.role === TRANSFORMATION_LEAD_ROLE
                        ) {
                          setLeadRevocation({ tenantId, change });
                          return;
                        }
                        applyChange.mutate({
                          userId: change.userId,
                          kind: change.kind,
                          role: change.role,
                        });
                      }}
                    >
                      {change.kind === "grant" ? "Approve" : "Remove"}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </SettingsGroup>

      {blocked.length > 0 ? (
        <SettingsGroup
          title="Held back"
          description="Changes your rules imply that cannot be applied yet."
        >
          <Card>
            <CardBody>
              <ul className="flex flex-col gap-2">
                {blocked.map((change) => (
                  <li
                    key={`${change.userId}-${change.kind}-blocked`}
                    className="border-b border-pb-border/70 pb-2 last:border-0"
                  >
                    <p className="text-sm font-medium">
                      {personLabel(change)}{" "}
                      <Badge>
                        {change.kind === "grant" ? "Grant" : "Removal"}
                      </Badge>
                    </p>
                    <p className="text-xs text-pb-text-muted">
                      {BLOCK_COPY[change.blockedBy]}
                    </p>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </SettingsGroup>
      ) : null}

      <SettingsGroup
        title="Rules"
        description="One rule per group. Retiring a rule stops it proposing changes; it does not remove access anyone already has."
      >
        <Card>
          <CardBody className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-2">
              <Input
                label="Find a group"
                placeholder="Search by name"
                value={groupQuery}
                onChange={(event) => setGroupQuery(event.target.value)}
              />
              <Select
                label="Directory group"
                placeholder={
                  unmappedGroups.length === 0
                    ? "No unmapped groups"
                    : "Choose a group"
                }
                options={unmappedGroups.map((group) => ({
                  value: group.groupId,
                  label: `${group.displayName} (${group.memberCount})`,
                }))}
                value={newGroupId}
                disabled={groups.isPending || unmappedGroups.length === 0}
                onChange={setNewGroupId}
              />
              <Select
                label="Role"
                options={ROLE_OPTIONS}
                value={newRole}
                onChange={(value) => setNewRole(asMappableRole(value))}
              />
              <Button
                variant="primary"
                disabled={newGroupId === "" || createRule.isPending}
                onClick={() =>
                  createRule.mutate({ groupId: newGroupId, role: newRole })
                }
              >
                Add rule
              </Button>
            </div>

            {groups.data?.nextCursor ? (
              <p className="text-xs text-pb-text-muted">
                More groups match than fit in this list — search by name to
                narrow it.
              </p>
            ) : null}

            {rules.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : mappings && mappings.length === 0 ? (
              <EmptyState
                variant="first-use"
                title="No rules yet"
                description="Choose a directory group above and say what it should make its members."
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {(mappings ?? []).map((mapping) => (
                  <li
                    key={mapping.mappingId}
                    className="flex flex-wrap items-center justify-between gap-3 border-b border-pb-border/70 pb-3 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {mapping.groupDisplayName}
                      </p>
                      <p className="text-xs text-pb-text-muted">
                        {mapping.memberCount}{" "}
                        {mapping.memberCount === 1 ? "person" : "people"} · from{" "}
                        {mapping.groupSource}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        label={`Role for ${mapping.groupDisplayName}`}
                        options={ROLE_OPTIONS}
                        value={mapping.role}
                        disabled={
                          changeRule.isPending &&
                          changeRule.variables?.groupId === mapping.groupId
                        }
                        onChange={(value) =>
                          changeRule.mutate({
                            groupId: mapping.groupId,
                            role: asMappableRole(value),
                          })
                        }
                      />
                      <Button
                        variant="ghost"
                        disabled={
                          retireRule.isPending &&
                          retireRule.variables === mapping.groupId
                        }
                        onClick={() => retireRule.mutate(mapping.groupId)}
                      >
                        Retire
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {rules.hasNextPage ? (
              <Button
                variant="secondary"
                disabled={rules.isFetchingNextPage}
                onClick={() => void rules.fetchNextPage()}
              >
                {rules.isFetchingNextPage ? "Loading…" : "Show more rules"}
              </Button>
            ) : null}
          </CardBody>
        </Card>
      </SettingsGroup>

      <TransformationLeadRemovalDialog
        tenantId={tenantId}
        currentUserId={claims?.userId ?? null}
        member={
          leadRevocationTarget
            ? {
                userId: leadRevocationTarget.userId,
                label: personLabel(leadRevocationTarget),
              }
            : null
        }
        description="They left the group that granted the role, or its rule was retired. They keep their workspace access and their own assessment responses; everything they own in LAMA moves to the replacement you choose."
        onClose={() => setLeadRevocation(null)}
        onConfirm={async (removal) => {
          if (!leadRevocationTarget) return;
          await applyChange.mutateAsync({
            userId: leadRevocationTarget.userId,
            kind: "revoke",
            role: TRANSFORMATION_LEAD_ROLE,
            removal,
          });
          setLeadRevocation(null);
        }}
      />
    </AdminSection>
  );
}
